import React from 'react';
import {
  Star,
  ShoppingCart,
  Heart,
  Eye,
  Zap,
  Truck,
} from 'lucide-react';

const FavoritesPage = ({ favorites, removeFromFavorites, addToCart, setCurrentPage }) => {
  // إذا كانت قائمة المفضلة فارغة، اعرض رسالة مناسبة
  if (favorites.length === 0) {
    return (
      <div className="container mx-auto px-4 py-24 text-center font-['Cairo']">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#10B981]/10">
          <Heart className="h-9 w-9 text-[#10B981]" />
        </div>
        <h2 className="mb-3 text-3xl font-bold text-gray-900 lg:text-4xl">المنتجات المفضلة</h2>
        <p className="mx-auto max-w-md text-lg text-gray-500">
          قائمة المفضلة فارغة حاليًا. استعرض منتجاتنا وأضف ما يعجبك!
        </p>
        <button
          onClick={() => setCurrentPage('home')}
          className="mt-8 rounded-xl bg-[#10B981] px-8 py-3.5 font-bold text-white shadow-sm shadow-[#10B981]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0ea173] hover:shadow-lg hover:shadow-[#10B981]/25"
        >
          العودة إلى المتجر
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16 font-['Cairo'] lg:py-20">
      <h2 className="mb-10 text-center text-3xl font-bold text-gray-900 lg:text-4xl">المنتجات المفضلة</h2>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {favorites.map((product) => (
          <div
            key={product.id}
            className="group overflow-hidden rounded-2xl bg-white shadow-md ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:ring-[#10B981]/20"
          >
            <div className="relative">
              <div className="relative flex h-64 items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                <img
                  src={product.imageSrc}
                  alt={product.name}
                  className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-110"
                />

                {product.badge && (
                  <div
                    className={`absolute right-3 top-3 rounded-full ${product.badgeColor} px-3 py-1 text-xs font-semibold text-white shadow-sm`}
                  >
                    {product.badge}
                  </div>
                )}

                <div className="absolute left-3 top-3 flex translate-x-[-4px] flex-col gap-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100">
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-sm transition-all duration-200 hover:bg-white hover:shadow-md active:scale-95"
                    onClick={() => removeFromFavorites(product.id)}
                    aria-label="إزالة من المفضلة"
                  >
                    <Heart className="h-4 w-4 fill-current text-red-500" />
                  </button>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-sm transition-all duration-200 hover:bg-white hover:shadow-md active:scale-95"
                    aria-label="عرض سريع"
                  >
                    <Eye className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <p className="mb-1 text-sm text-gray-500">{product.brand}</p>
                <h3 className="mb-2 line-clamp-2 text-lg font-bold text-gray-900 transition-colors group-hover:text-[#10B981]">
                  {product.name}
                </h3>

                <div className="mb-3 flex items-center gap-2">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${i < Math.floor(product.rating) ? 'fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">
                    {product.rating} ({product.reviews})
                  </span>
                </div>

                <div className="mb-4 flex items-center gap-2">
                  <span className="text-2xl font-bold text-[#10B981]">{product.price} ر.س</span>
                  {product.originalPrice && (
                    <span className="text-base text-gray-400 line-through">
                      {product.originalPrice} ر.س
                    </span>
                  )}
                </div>

                <div className="mb-4 flex items-center gap-4 text-sm">
                  {product.fastDelivery && (
                    <div className="flex items-center gap-1 text-[#10B981]">
                      <Truck className="h-4 w-4" />
                      <span>توصيل سريع</span>
                    </div>
                  )}
                  {product.inStock && (
                    <div className="flex items-center gap-1 text-indigo-600">
                      <Zap className="h-4 w-4" />
                      <span>متوفر</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => product.inStock && addToCart(product)}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-bold transition-all duration-300 ${
                    product.inStock
                      ? 'bg-[#10B981] text-white shadow-sm shadow-[#10B981]/20 hover:bg-[#0ea173] hover:shadow-lg hover:shadow-[#10B981]/25 active:scale-[0.98]'
                      : 'cursor-not-allowed bg-gray-200 text-gray-400'
                  }`}
                  disabled={!product.inStock}
                >
                  <ShoppingCart className="h-4 w-4" />
                  {product.inStock ? 'أضف للسلة' : 'غير متوفر'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FavoritesPage;
